/**
 * asad-pdp-gallery.js
 * Nahhar Fashions — PDP Gallery Video Autoplay + Variant Media Sync
 *
 * Problem: Dawn's variant-picker fetches a new section render on variant change.
 * media-gallery.js replaces itself with the new HTML (which has variant's image first).
 * BUT our NF video-first sort always moves video to position 0, so after the replace,
 * the video is still slide 0 even if the user selected a colour with an image.
 *
 * Solution:
 * 1. Listen for variant:update → get the featured_media.id
 * 2. After Dawn's replaceWith() settles, find the slide whose [data-media-id] matches
 * 3. Navigate the slideshow to that slide index via Dawn's select() API
 */
(function () {
  'use strict';

  /* ── Helpers ─────────────────────────────────────────────── */

  /**
   * Get the Slideshow custom element from inside the media-gallery.
   * Dawn uses <slideshow-component> as the tag name.
   * @returns {Element|null}
   */
  function getSlideshow() {
    var gallery = document.querySelector('media-gallery');
    return gallery ? gallery.querySelector('slideshow-component') : null;
  }

  /**
   * Find which slide index has a data-media-id matching the given mediaId.
   * The slides inside the slideshow are <slideshow-slide> elements.
   * @param {string|number} mediaId
   * @returns {number} -1 if not found
   */
  function findSlideIndexByMediaId(mediaId) {
    var slideshow = getSlideshow();
    if (!slideshow) return -1;

    var slides = slideshow.querySelectorAll('slideshow-slide');
    var found = -1;

    slides.forEach(function (slide, i) {
      // The product-media div inside the slide carries data-media-id
      var mediaDivId = slide.querySelector('[data-media-id]');
      if (mediaDivId) {
        var id = mediaDivId.dataset.mediaId;
        if (id && String(id) === String(mediaId)) {
          found = i;
        }
      }
    });

    return found;
  }

  /**
   * Programmatically select a slide by numeric index.
   * Uses Dawn's slideshow.select() if available, falls back to thumbnail click.
   * @param {number} index
   */
  function selectSlide(index) {
    if (index < 0) return;

    var slideshow = getSlideshow();
    if (!slideshow) return;

    // Dawn's slideshow.select(index) — numeric index, instant jump (no sweep animation)
    if (typeof slideshow.select === 'function') {
      slideshow.select(index, undefined, { animate: false });
      return;
    }

    // Fallback: click the thumbnail button at the matching index
    var gallery = document.querySelector('media-gallery');
    if (!gallery) return;
    var thumbBtns = gallery.querySelectorAll('[data-slideshow-controls] button, [ref="controls[]"] button');
    if (thumbBtns[index]) {
      thumbBtns[index].click();
    }
  }

  /* ── Part 1: Variant → Gallery Slide Sync ─────────────── */

  function handleVariantUpdate(event) {
    var detail = event && event.detail;
    if (!detail) return;

    // Ignore if this fired inside the quick-add dialog — handled separately
    if (event.target && event.target.closest && event.target.closest('#quick-add-dialog')) return;

    // featured_media lives on resource
    var featuredMedia = detail.resource && detail.resource.featured_media;
    if (!featuredMedia || !featuredMedia.id) return;

    var mediaId = featuredMedia.id;

    // Wait for Dawn's replaceWith() + custom element upgrade to fully settle.
    // Two rAFs + a small timeout gives the new slideshow-component time to connect.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        setTimeout(function () {
          var idx = findSlideIndexByMediaId(mediaId);
          // idx >= 0 covers ALL valid positions including index 0
          if (idx >= 0) {
            selectSlide(idx);
          }
        }, 50);
      });
    });
  }

  function initVariantSync() {
    // The variant:update event bubbles — listen at document level
    document.addEventListener('variant:update', handleVariantUpdate);
  }

  /* ── Part 2: PDP video autoplay on initial load ─────────── */

  function autoplayFirstPDPVideo() {
    var slideshow = getSlideshow();
    if (!slideshow) return;

    var firstSlide = slideshow.querySelector('slideshow-slide');
    if (!firstSlide) return;

    // Find deferred-media inside (Dawn's video player)
    var deferred = firstSlide.querySelector('deferred-media');
    if (deferred) {
      var playBtn = deferred.querySelector('button');
      if (playBtn) {
        playBtn.click();
        return;
      }
    }

    // Direct video fallback
    var video = firstSlide.querySelector('video');
    if (video) {
      video.muted = true;
      video.loop = true;
      video.setAttribute('playsinline', '');
      video.play().catch(function () {});
    }
  }

  /* ── Part 3: Product card video autoplay when in view ─── */

  function initCardVideoAutoplay() {
    if (!('IntersectionObserver' in window)) return;

    var cards = document.querySelectorAll('.asad-product-card__media');
    if (!cards.length) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target.querySelector('video');
        if (!video) return;
        if (entry.isIntersecting) {
          video.muted = true;
          video.loop = true;
          video.setAttribute('playsinline', '');
          video.play().catch(function () {});
        } else {
          video.pause();
        }
      });
    }, { threshold: 0.3 });

    cards.forEach(function (card) { observer.observe(card); });
  }

  /* ── Part 4: Quick-add dialog variant image sync ─────── */
  /**
   * When a variant is selected inside the quick-add dialog, the Section Rendering
   * API only returns the variant-picker HTML (section-rendering-product-card).
   * It does NOT contain a media-gallery, so media-gallery.js can't swap the image.
   *
   * Fix: Listen for variant:update inside #quick-add-dialog and scroll the
   * .product-information__media panel to the correct slideshow-slide.
   */
  function handleQuickAddVariantUpdate(event) {
    var detail = event && event.detail;
    if (!detail) return;

    var featuredMedia = detail.resource && detail.resource.featured_media;
    if (!featuredMedia || !featuredMedia.id) return;

    var mediaId = String(featuredMedia.id);
    var dialog = document.getElementById('quick-add-dialog');
    if (!dialog) return;

    var mediaPanel = dialog.querySelector('.product-information__media');
    if (!mediaPanel) return;

    var slides = mediaPanel.querySelectorAll('slideshow-slide');
    var targetSlide = null;

    slides.forEach(function (slide) {
      var mediaEl = slide.querySelector('[data-media-id]');
      if (mediaEl && String(mediaEl.dataset.mediaId) === mediaId) {
        targetSlide = slide;
      }
      // Remove previous active highlight
      slide.removeAttribute('data-nf-active');
    });

    if (!targetSlide) return;

    // Mark active slide
    targetSlide.setAttribute('data-nf-active', 'true');

    // Instant-jump scroll (no sweep) to the target slide in the media panel
    var panelTop = mediaPanel.getBoundingClientRect().top;
    var slideTop = targetSlide.getBoundingClientRect().top;
    var currentScroll = mediaPanel.scrollTop;
    var targetScroll = currentScroll + (slideTop - panelTop);

    // Use instant scroll so user sees the correct image immediately
    mediaPanel.scrollTo({ top: targetScroll, behavior: 'instant' });
  }

  function initQuickAddVariantSync() {
    // Listen for variant:update that originates inside the quick-add dialog
    var dialog = document.getElementById('quick-add-dialog');
    if (!dialog) {
      // Dialog might not be in DOM yet — use document-level delegation
      document.addEventListener('variant:update', function (event) {
        if (event.target && event.target.closest && event.target.closest('#quick-add-dialog')) {
          handleQuickAddVariantUpdate(event);
        }
      });
      return;
    }
    dialog.addEventListener('variant:update', handleQuickAddVariantUpdate);
  }

  /* ── Part 5: Quick-add dialog — inject description & trust badges ── */

  /**
   * When the quick-add modal opens, find the product handle from the dialog,
   * fetch the description via the Shopify Products API, and inject it below
   * the variant picker inside the modal's info column.
   */
  function injectQuickAddDescription(dialog) {
    if (!dialog) return;

    // Avoid duplicate injections
    if (dialog.querySelector('.nf-qa-desc')) return;

    // Get product handle from the "View full details" link or data attribute
    var viewLink = dialog.querySelector('a[href*="/products/"]');
    if (!viewLink) return;

    var href = viewLink.getAttribute('href') || '';
    var match = href.match(/\/products\/([^?#/]+)/);
    if (!match) return;
    var handle = match[1];

    // Fetch product JSON
    fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (product) {
        // Re-check dialog is still open and hasn't been injected yet
        if (!dialog.querySelector('.nf-qa-desc') && product.description) {
          appendQuickAddInfo(dialog, product);
        }
      })
      .catch(function () {});
  }

  function appendQuickAddInfo(dialog, product) {
    // Find the product info column (right side of modal)
    var infoCol = dialog.querySelector('.product-information__info, .quick-add-modal__content .product-details, .quick-add-modal__content .product-info, form[action*="/cart/add"]');
    if (!infoCol) {
      // Fallback: find the form and insert after it
      infoCol = dialog.querySelector('form');
    }
    if (!infoCol) return;

    // Build description block
    var desc = document.createElement('div');
    desc.className = 'nf-qa-desc';
    desc.innerHTML =
      '<div class="nf-qa-desc__body">' + product.description + '</div>' +
      '<div class="nf-qa-desc__badges">' +
        '<span>✦ 100% Handmade</span>' +
        '<span>✦ Free Shipping ₹999+</span>' +
        '<span>✦ 7-Day Returns</span>' +
      '</div>';

    infoCol.insertAdjacentElement('afterend', desc);
  }

  function initQuickAddDescriptionInjection() {
    // Watch for the quick-add dialog to open (it gets the [open] attribute)
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type !== 'attributes' || mutation.attributeName !== 'open') return;
        var dialog = mutation.target;
        if (!dialog || !dialog.id || dialog.id.indexOf('quick-add') === -1) return;

        if (dialog.hasAttribute('open')) {
          // Modal just opened — wait a tick for content to render
          setTimeout(function () { injectQuickAddDescription(dialog); }, 300);
        } else {
          // Modal closed — remove injected content so it's fresh next time
          var old = dialog.querySelector('.nf-qa-desc');
          if (old) { old.remove(); }
        }
      });
    });

    // Observe all quick-add dialogs (may be multiple in DOM)
    function observeDialogs() {
      var dialogs = document.querySelectorAll('[id^="quick-add"]');
      dialogs.forEach(function (d) {
        observer.observe(d, { attributes: true });
      });
    }

    observeDialogs();

    // Also observe body for dynamically added dialogs
    var bodyObserver = new MutationObserver(function () { observeDialogs(); });
    bodyObserver.observe(document.body, { childList: true, subtree: false });
  }

  /* ── Part 6: Variant-Specific Gallery Filter ─────────── */

  /**
   * Reads the embedded JSON data script for a given media-gallery element.
   * @param {Element} gallery
   * @returns {object|null}
   */
  function getNfGalleryData(gallery) {
    if (!gallery) {
      return null;
    }
    var blockId = gallery.dataset.nfBlockId;
    if (!blockId) {
      return null;
    }
    var script = document.getElementById('nf-gallery-data-' + blockId);
    if (!script) {
      return null;
    }
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  /**
   * Builds the variant filter map from the embedded JSON.
   * @param {object} data - Parsed gallery JSON
   * @param {number|string} [overrideVariantId] - Use this variant ID instead of data.selectedVariantId
   * @returns {object|null} map with allOptionValues, selectedOptions, variantMediaIds, selectedMediaId
   */
  function buildVariantMap(data, overrideVariantId) {
    if (!data || !data.variants || !data.variants.length) {
      return null;
    }

    var allVariants = data.variants;
    var selectedVariantId = Number(overrideVariantId || data.selectedVariantId);
    var selectedVariant = null;

    allVariants.forEach(function (v) {
      if (v.id === selectedVariantId) {
        selectedVariant = v;
      }
    });

    if (!selectedVariant) {
      return null;
    }

    // All known option values across all variants (lowercased) — e.g. colors
    var allOptionValues = {};
    allVariants.forEach(function (v) {
      ['option1', 'option2', 'option3'].forEach(function (opt) {
        if (v[opt]) {
          allOptionValues[v[opt].toLowerCase().trim()] = true;
        }
      });
    });

    // Selected variant's option values (lowercased)
    var selectedOptions = {};
    ['option1', 'option2', 'option3'].forEach(function (opt) {
      if (selectedVariant[opt]) {
        selectedOptions[selectedVariant[opt].toLowerCase().trim()] = true;
      }
    });

    // All variant featured media IDs (as strings)
    var variantMediaIds = {};
    allVariants.forEach(function (v) {
      var mid = String(v.featuredMediaId || '');
      if (mid && mid !== 'null') {
        variantMediaIds[mid] = true;
      }
    });

    var selectedMediaId = String(selectedVariant.featuredMediaId || '');
    if (selectedMediaId === 'null') {
      selectedMediaId = '';
    }

    return {
      allOptionValues: allOptionValues,
      selectedOptions: selectedOptions,
      variantMediaIds: variantMediaIds,
      selectedMediaId: selectedMediaId,
    };
  }

  /**
   * Determines if a media slide should be visible for the current variant.
   * @param {string} mediaType - e.g. 'image', 'video', 'external_video', 'model'
   * @param {string} mediaAlt  - lowercased alt text from data-nf-media-alt
   * @param {string} mediaId   - media ID from data-nf-media-id
   * @param {object} map       - result of buildVariantMap()
   * @returns {boolean}
   */
  function isMediaVisible(mediaType, mediaAlt, mediaId, map) {
    if (!map) {
      return true;
    }

    // A+ listing images are always excluded from the gallery (rendered below the fold)
    if ((mediaAlt || '').trim() === 'aplus') {
      return false;
    }

    // 3D models — always visible
    if (mediaType === 'model') {
      return true;
    }

    // Smart video logic:
    //   • No alt text, or alt doesn't match any variant option
    //       → untagged / product-level video → always show in all variants
    //   • Alt text matches a variant option (e.g. "Red")
    //       → variant-specific video → falls through to alt-matching below
    // This means:
    //   1 video, no alt        → shows in ALL variants  (current behaviour preserved)
    //   2 videos, 1 tagged     → untagged shows always, tagged shows only its variant
    //   3 videos, all tagged   → each shows only for its matched variant
    if (mediaType === 'video' || mediaType === 'external_video') {
      var videoAlt = (mediaAlt || '').trim();
      if (!videoAlt || !map.allOptionValues[videoAlt]) {
        return true; // untagged → always show
      }
      // Tagged to a variant → fall through to alt-matching logic below
    }

    var alt = (mediaAlt || '').trim();
    var mid = String(mediaId || '');

    // Alt text matches a known variant option value
    if (alt && map.allOptionValues[alt]) {
      // Show only if it matches the selected variant's options
      return !!map.selectedOptions[alt];
    }

    // Media is a specific variant's featured_media
    if (mid && map.variantMediaIds[mid]) {
      // Show only if it's the selected variant's featured media
      return mid === map.selectedMediaId;
    }

    // Product-level image (not tagged to any variant) — always show
    return true;
  }

  /**
   * Apply the variant filter to a media-gallery element:
   * hides slides + thumbnails whose media belongs to a different variant.
   * @param {Element} gallery - The <media-gallery> element
   */
  function applyGalleryFilter(gallery) {
    if (!gallery) {
      return;
    }

    var data = getNfGalleryData(gallery);
    var map = buildVariantMap(data);

    // No variant data? Nothing to filter (likely no variant-specific images)
    if (!map) {
      return;
    }

    var slideshow = gallery.querySelector('slideshow-component');
    if (!slideshow) {
      return;
    }

    var slides = slideshow.querySelectorAll('slideshow-slide');
    var thumbBtns = gallery.querySelectorAll('.slideshow-controls__thumbnail');
    // Dot controls for mobile pagination (non-thumbnail slideshow-control buttons)
    var dotBtns = gallery.querySelectorAll('slideshow-controls .slideshow-control:not(.slideshow-controls__thumbnail)');
    var firstVisibleIdx = -1;
    var currentActiveIdx = -1;

    // Find the currently active slide index
    slides.forEach(function (slide, i) {
      if (slide.getAttribute('aria-hidden') === 'false') {
        currentActiveIdx = i;
      }
    });

    slides.forEach(function (slide, i) {
      var mediaType = slide.dataset.nfMediaType || '';
      var mediaAlt  = slide.dataset.nfMediaAlt  || '';
      // data-nf-media-id is on the slide; product-media div also has data-media-id
      var mediaId   = slide.dataset.nfMediaId   || '';
      if (!mediaId) {
        var mediaEl = slide.querySelector('[data-media-id]');
        if (mediaEl) {
          mediaId = mediaEl.dataset.mediaId || '';
        }
      }

      var visible = isMediaVisible(mediaType, mediaAlt, mediaId, map);

      if (visible) {
        slide.removeAttribute('data-nf-hidden');
        if (firstVisibleIdx < 0) {
          firstVisibleIdx = i;
        }
      } else {
        slide.setAttribute('data-nf-hidden', '');
      }

      // Sync the thumbnail (desktop strip) and dot (mobile) at the same position
      if (thumbBtns[i]) {
        if (visible) {
          thumbBtns[i].removeAttribute('data-nf-hidden');
        } else {
          thumbBtns[i].setAttribute('data-nf-hidden', '');
        }
      }
      if (dotBtns[i]) {
        if (visible) {
          dotBtns[i].removeAttribute('data-nf-hidden');
          dotBtns[i].removeAttribute('hidden');
        } else {
          dotBtns[i].setAttribute('data-nf-hidden', '');
          dotBtns[i].setAttribute('hidden', '');
        }
      }
    });

    // Safety fallback: if ALL slides were hidden (no alt texts configured yet),
    // remove the filter entirely so the gallery doesn't go blank
    if (firstVisibleIdx < 0) {
      slides.forEach(function (slide, i) {
        slide.removeAttribute('data-nf-hidden');
        if (thumbBtns[i]) {
          thumbBtns[i].removeAttribute('data-nf-hidden');
        }
        if (dotBtns[i]) {
          dotBtns[i].removeAttribute('data-nf-hidden');
          dotBtns[i].removeAttribute('hidden');
        }
      });
      return;
    }

    // If the currently active slide is now hidden, navigate to first visible
    if (firstVisibleIdx >= 0 && currentActiveIdx >= 0) {
      var activeSlide = slides[currentActiveIdx];
      if (activeSlide && activeSlide.hasAttribute('data-nf-hidden')) {
        selectSlide(firstVisibleIdx);
      }
    }
  }

  /**
   * Initialise the variant gallery filter.
   * Uses MutationObserver on the gallery's parent so the filter fires the INSTANT
   * Dawn's replaceWith() inserts the new <media-gallery> element — no setTimeout race.
   * Self-reconnects after each replacement so it keeps watching forever.
   * @param {Element} gallery
   */
  function applyAndObserveGallery(gallery) {
    if (!gallery) {
      return;
    }

    // Apply filter to this gallery right away
    applyGalleryFilter(gallery);

    var parent = gallery.parentElement;
    if (!parent) {
      return;
    }

    // Watch the parent for when Dawn replaces this gallery via replaceWith()
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type !== 'childList') {
          return;
        }
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeName === 'MEDIA-GALLERY') {
            // Dawn just swapped in a fresh <media-gallery> — stop watching the old parent
            observer.disconnect();
            // Wait one rAF so the slideshow custom element can connect + render
            requestAnimationFrame(function () {
              applyAndObserveGallery(node);
            });
          }
        });
      });
    });

    observer.observe(parent, { childList: true });
  }

  function initVariantGalleryFilter() {
    requestAnimationFrame(function () {
      applyAndObserveGallery(document.querySelector('media-gallery'));
    });

    // ── Optimistic filter (INSTANT — fires before network fetch) ───────────
    // 'variant:selected' fires the moment user taps a swatch, before Dawn's
    // Section Rendering API call. Filters the current gallery immediately so
    // the switch feels instant. MutationObserver re-applies to the fresh
    // gallery once replaceWith() completes for final correctness.
    document.addEventListener('variant:selected', function (event) {
      var newVariant = event.detail && event.detail.resource;
      if (!newVariant || !newVariant.id) {
        return;
      }
      var gallery = document.querySelector('media-gallery');
      if (!gallery) {
        return;
      }
      var data = getNfGalleryData(gallery);
      if (!data) {
        return;
      }
      var map = buildVariantMap(data, newVariant.id);
      if (!map) {
        return;
      }

      var slideshow = gallery.querySelector('slideshow-component');
      if (!slideshow) {
        return;
      }
      var slides = slideshow.querySelectorAll('slideshow-slide');
      var thumbBtns = gallery.querySelectorAll('.slideshow-controls__thumbnail');
      var firstVisibleIdx = -1;

      slides.forEach(function (slide, i) {
        var mediaType = slide.dataset.nfMediaType || '';
        var mediaAlt  = slide.dataset.nfMediaAlt  || '';
        var mediaId   = slide.dataset.nfMediaId   || '';
        if (!mediaId) {
          var mediaEl = slide.querySelector('[data-media-id]');
          if (mediaEl) {
            mediaId = mediaEl.dataset.mediaId || '';
          }
        }

        var visible = isMediaVisible(mediaType, mediaAlt, mediaId, map);

        if (visible) {
          slide.removeAttribute('data-nf-hidden');
          if (firstVisibleIdx < 0) {
            firstVisibleIdx = i;
          }
        } else {
          slide.setAttribute('data-nf-hidden', '');
        }

        if (thumbBtns[i]) {
          if (visible) {
            thumbBtns[i].removeAttribute('data-nf-hidden');
          } else {
            thumbBtns[i].setAttribute('data-nf-hidden', '');
          }
        }
      });

      // Safety: if all hidden, show everything (no alt texts configured yet)
      if (firstVisibleIdx < 0) {
        slides.forEach(function (slide, i) {
          slide.removeAttribute('data-nf-hidden');
          if (thumbBtns[i]) {
            thumbBtns[i].removeAttribute('data-nf-hidden');
          }
        });
      }
    });
  }

  /* ── Init ─────────────────────────────────────────────── */

  function init() {
    var isProductPage = !!document.querySelector('media-gallery');

    if (isProductPage) {
      setTimeout(autoplayFirstPDPVideo, 900);
      initVariantSync();
      initVariantGalleryFilter();
    }

    initCardVideoAutoplay();
    initQuickAddVariantSync();
    initQuickAddDescriptionInjection();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
